<template>
  <div
    :class="[
      {
        'has-error': localErrors.length,
        'disabled' : disabled,
        'loading': loading,
        'small': size === 'small'
      },
      'checkbox'
    ]"
  >
    <input
      :id="id"
      type="checkbox"
      :value="value"
      :required="required"
      :disabled="disabled"
      :checked="checked"
      @change="updateValue"
    >

    <label
      v-if="label !== null"
      :for="id"
      class="font-smoothing"
      v-html="label"
    />

    <slot />

    <x-errors-messages :errors="localErrors" />

    <div class="spinner">
      <md-progress-spinner
        v-if="loading"
        :md-stroke="2"
        :md-diameter="15"
        md-mode="indeterminate"
        class="md-accent"
      />
    </div>
  </div>
</template>

<script>
  import xErrorsMessages from './x-errors-messages'

  let checkboxId = 0

  export default {
    name: 'XCheckbox',

    components: {
      'x-errors-messages': xErrorsMessages
    },

    props: {
      value: {},

      checked: {
        type: Boolean,
        default: false
      },

      label: {
        type: String,
        default: null
      },

      required: {
        type: Boolean,
        default: false
      },

      disabled: {
        type: Boolean,
        default: false
      },

      loading: {
        type: Boolean,
        required: false,
        default: false
      },

      errors: {
        type: Array,
        default: () => []
      },

      size: {
        type: String,
        default: 'normal'
      }
    },

    data () {
      return {
        id: `x-checkbox-${checkboxId++}`,
        localErrors: []
      }
    },

    watch: {
      errors () {
        this.localErrors = this.errors
      }
    },

    methods: {
      updateValue (event) {
        this.localErrors = []
        this.$emit('update', this.value ? event.target.value : event.target.checked)
      }
    }
  }
</script>

<style lang='scss' scoped rel='stylesheet/scss'>
  @import '~@/styles/vars.scss';

  $checkbox-width: 2rem;

  .checkbox {
    position: relative;
    cursor: pointer;

    &.disabled {
      label {
        cursor: not-allowed;
      }
    }

    &.loading.slider {
      pointer-events: none;

      label,
      input {
        transition: opacity 0.5s ease-in;
        opacity: 0.3;
      }
      input:checked {
        + label {
          background-color: $color-ghost;
          border-color: $color-ghost;
        }
      }
    }

    .spinner {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      pointer-events: none;
    }
  }

  input {
    margin: 0;
    padding: 0;
    appearance: none;
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }

  input:focus {
    outline: 0;
  }

  .has-error {
    input {
      border-color: $color-red;
    }

    label {
      color: $color-red;
    }
  }

  .errors-messages {
    padding: 0.6rem 1.4rem;
    font-size: 1.4rem;
  }

  // Classic checkbox
  @mixin checkbox-style(
    $color,
    $background,
    $border,
    $background-disabled,
    $background-checked: map_get($palette-primary, main),
    $border-checked: map_get($palette-primary, main)
  ) {
    display: inline-flex;
    align-items: center;

    label {
      display: block;
      padding: $space-small $space-small $space-small $space-big;
      font-size: 1.4rem;
      font-weight: 600;
      color: $color;
      z-index: 2;
    }

    input {
      width: $checkbox-width;
      height: $checkbox-width;
      border-radius: 2px;
      background-color: $background;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      border: 2px solid $border;
      transition: all 0.3s ease-in-out;

      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        background-image: url('~@/assets/icn-checked.svg');
        background-size: $checkbox-width*0.75;
        background-repeat: no-repeat;
        background-position: 20%;
        height: $checkbox-width;
        width: $checkbox-width;
        opacity: 0;
      }
    }

    input:disabled {
      background-color: $background-disabled;
    }

    input:checked {
      background-color: $background-checked;
      border-color: $border-checked;

      &:before {
        opacity: 1;
      }
    }
  }

  .checkbox:not(.slider) {
    @include checkbox-style(
      $color-daddy,
      white,
      $color-ghost,
      $color-whisper
    )
  }

  .checkbox:not(.slider).transparent-white {
    @include checkbox-style(
      white,
      transparent,
      white,
      $color-whisper,
      transparent,
      white
    )
  }

  // Slider checkbox

  .checkbox.slider {
    display: inline-block;

    label {
      display: inline-block;
      font-size: 0;
      width: 6rem;
      padding: 0;
      height: 2.8rem;
      border-radius: 2rem;
      transition: background 120ms ease-in-out, border 120ms ease-in-out;
      border: 2px solid;
      background-color: $color-ghost;
      border-color: $color-ghost;
    }

    input {
      position: absolute;
      background-color: white;
      border-radius: 100%;
      height: 2.4rem;
      width: 2.4rem;
      top: 0.2rem;
      left: 0.2rem;
      transition: transform 120ms ease-in-out;
    }

    input:checked {
      transform: translateX(3.2rem);

      + label {
        background-color: map_get($palette-primary, main);
        border-color: map_get($palette-primary, main);
      }
    }

    &.disabled {
      opacity: 0.2;
      pointer-events: none;
    }

    &.switch {
      input {
        height: 9rem;
        width: 9rem;
        top: 0.5rem;
        left: 0.5rem;

        &:checked {
          left: auto;
          right: 0.5rem;
          transform: none;
        }
      }

      label {
        width: 25rem;
        height: 10rem;
        border-radius: 10rem;
      }
    }

    &.small {
      input {
        width: 1.2rem;
        height: 1.2rem;

        &:checked {
          transform: translateX(1.6rem);
        }
      }

      label {
        height: 1.6rem;
        width: 3.2rem;
      }
    }
  }
</style>
